<template>
  <div class="error-state">
    <!-- Alert Error -->
    <v-alert
      v-if="type === 'alert'"
      :type="alertType"
      :outlined="outlined"
      :dismissible="dismissible"
      :value="showError"
      @input="onDismiss"
    >
      <div class="d-flex align-center">
        <div class="flex-grow-1">
          <div v-if="title" class="text-subtitle-1 font-weight-bold">{{ title }}</div>
          <div>{{ message }}</div>
          <div v-if="details" class="text-caption mt-1">{{ details }}</div>
        </div>
        <div v-if="showRetry" class="ml-4">
          <v-btn
            small
            :color="retryColor"
            @click="onRetry"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </div>
      </div>
    </v-alert>

    <!-- Card Error -->
    <v-card v-else-if="type === 'card'" :outlined="outlined">
      <v-card-text class="text-center pa-8">
        <v-icon
          :size="iconSize"
          :color="iconColor"
          class="mb-4"
        >
          {{ errorIcon }}
        </v-icon>

        <div v-if="title" class="text-h6 mb-2">{{ title }}</div>
        <div class="text-body-1 mb-4">{{ message }}</div>
        <div v-if="details" class="text-body-2 grey--text mb-4">{{ details }}</div>

        <div v-if="showActions" class="d-flex justify-center flex-wrap">
          <v-btn
            v-if="showRetry"
            :color="retryColor"
            class="ma-1"
            @click="onRetry"
          >
            <v-icon left>mdi-refresh</v-icon>
            Try Again
          </v-btn>

          <v-btn
            v-if="showHome"
            color="primary"
            text
            class="ma-1"
            @click="goHome"
          >
            <v-icon left>mdi-home</v-icon>
            Go Home
          </v-btn>

          <v-btn
            v-if="showSupport"
            color="info"
            text
            class="ma-1"
            @click="contactSupport"
          >
            <v-icon left>mdi-help-circle</v-icon>
            Contact Support
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Inline Error -->
    <div v-else-if="type === 'inline'" class="inline-error d-flex align-center">
      <v-icon
        :color="iconColor"
        :size="iconSize"
        class="mr-2"
      >
        {{ errorIcon }}
      </v-icon>

      <div class="flex-grow-1">
        <div v-if="title" class="text-subtitle-2 font-weight-bold">{{ title }}</div>
        <div class="text-body-2">{{ message }}</div>
        <div v-if="details" class="text-caption grey--text">{{ details }}</div>
      </div>

      <div v-if="showRetry" class="ml-2">
        <v-btn
          small
          icon
          :color="retryColor"
          @click="onRetry"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Banner Error -->
    <v-banner
      v-else-if="type === 'banner'"
      :value="showError"
      :color="bannerColor"
      :icon="errorIcon"
      :outlined="outlined"
    >
      <div v-if="title" class="text-subtitle-1 font-weight-bold">{{ title }}</div>
      <div>{{ message }}</div>
      <div v-if="details" class="text-caption mt-1">{{ details }}</div>

      <template #actions>
        <v-btn
          v-if="showRetry"
          text
          :color="retryColor"
          @click="onRetry"
        >
          Retry
        </v-btn>
        <v-btn
          v-if="dismissible"
          text
          @click="onDismiss"
        >
          Dismiss
        </v-btn>
      </template>
    </v-banner>

    <!-- Empty State -->
    <div v-else-if="type === 'empty'" class="empty-state text-center pa-8">
      <v-icon
        :size="iconSize"
        :color="iconColor"
        class="mb-4"
      >
        {{ emptyIcon }}
      </v-icon>

      <div v-if="title" class="text-h6 mb-2">{{ title }}</div>
      <div class="text-body-1 mb-4">{{ message }}</div>
      <div v-if="details" class="text-body-2 grey--text mb-4">{{ details }}</div>

      <div v-if="showActions">
        <v-btn
          v-if="showCreate"
          :color="createColor"
          @click="onCreate"
        >
          <v-icon left>{{ createIcon }}</v-icon>
          {{ createText }}
        </v-btn>

        <v-btn
          v-if="showRefresh"
          color="primary"
          text
          class="ml-2"
          @click="onRetry"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Default Error -->
    <div v-else class="default-error text-center pa-4">
      <v-icon
        :color="iconColor"
        :size="iconSize"
        class="mb-2"
      >
        {{ errorIcon }}
      </v-icon>

      <div v-if="title" class="text-subtitle-1 font-weight-bold mb-1">{{ title }}</div>
      <div class="text-body-2">{{ message }}</div>

      <v-btn
        v-if="showRetry"
        small
        :color="retryColor"
        class="mt-2"
        @click="onRetry"
      >
        <v-icon left small>mdi-refresh</v-icon>
        Retry
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErrorState',

  props: {
    // Error display props
    type: {
      type: String,
      default: 'alert',
      validator: (value) => ['alert', 'card', 'inline', 'banner', 'empty', 'default'].includes(value),
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      required: true,
    },
    details: {
      type: String,
      default: '',
    },

    // Visual props
    outlined: {
      type: Boolean,
      default: false,
    },
    iconSize: {
      type: [Number, String],
      default: 64,
    },

    // Error type props
    errorType: {
      type: String,
      default: 'error',
      validator: (value) => ['error', 'warning', 'info', 'network', 'validation', 'permission', 'notFound'].includes(value),
    },

    // Action props
    showRetry: {
      type: Boolean,
      default: true,
    },
    showHome: {
      type: Boolean,
      default: false,
    },
    showSupport: {
      type: Boolean,
      default: false,
    },
    showCreate: {
      type: Boolean,
      default: false,
    },
    showRefresh: {
      type: Boolean,
      default: false,
    },
    dismissible: {
      type: Boolean,
      default: false,
    },

    // Action customization
    createText: {
      type: String,
      default: 'Create New',
    },
    createIcon: {
      type: String,
      default: 'mdi-plus',
    },
    createColor: {
      type: String,
      default: 'primary',
    },
    retryColor: {
      type: String,
      default: 'primary',
    },
  },

  data() {
    return {
      showError: true,
    };
  },

  computed: {
    alertType() {
      const typeMap = {
        error: 'error',
        warning: 'warning',
        info: 'info',
        network: 'error',
        validation: 'warning',
        permission: 'warning',
        notFound: 'info',
      };
      return typeMap[this.errorType] || 'error';
    },

    iconColor() {
      const colorMap = {
        error: 'error',
        warning: 'warning',
        info: 'info',
        network: 'error',
        validation: 'warning',
        permission: 'orange',
        notFound: 'grey',
      };
      return colorMap[this.errorType] || 'error';
    },

    bannerColor() {
      return this.iconColor;
    },

    errorIcon() {
      const iconMap = {
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information',
        network: 'mdi-wifi-off',
        validation: 'mdi-form-select',
        permission: 'mdi-lock',
        notFound: 'mdi-file-search',
      };
      return iconMap[this.errorType] || 'mdi-alert-circle';
    },

    emptyIcon() {
      const iconMap = {
        error: 'mdi-database-off',
        warning: 'mdi-database-alert',
        info: 'mdi-information',
        network: 'mdi-cloud-off',
        validation: 'mdi-form-select',
        permission: 'mdi-lock',
        notFound: 'mdi-magnify',
      };
      return iconMap[this.errorType] || 'mdi-database-off';
    },

    showActions() {
      return this.showRetry
        || this.showHome
        || this.showSupport
        || this.showCreate
        || this.showRefresh;
    },
  },

  methods: {
    onRetry() {
      this.$emit('retry');
    },

    onDismiss() {
      this.showError = false;
      this.$emit('dismiss');
    },

    onCreate() {
      this.$emit('create');
    },

    goHome() {
      this.$router.push('/');
    },

    contactSupport() {
      this.$emit('support');
      // Could also open a support dialog or redirect to support page
    },
  },
};
</script>

<style scoped>
.error-state {
  width: 100%;
}

.inline-error {
  padding: 8px 0;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.default-error {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
